import { Currency } from "@redotech/money/currencies";
import { getCurrencyFormatter } from "@redotech/money/util";
import * as z from "zod";
import {
  CompensationMethod,
  ReturnedProductStatus,
  ReturnTypeEnum,
} from "../return";
import { ReturnMethodType } from "../return-method";
import { ReturnStatus } from "../return-status";
import { PurchaseChannel } from "../warranties";
import {
  OrderProductColumnKey,
  PurchasedExtendedWarrantyDataColumnKey,
  ReturnDataColumnKey,
  ReturnProductColumnKey,
  WarrantyCustomerDataColumnKey,
} from "./reports/columns";
import { SalesChannelValues } from "./returns-claims/filters/sales-channel-values";

/**
 * Map of raw shipping provider values to display names
 */
const shippingProviderDisplayNames: Record<string, string | undefined> = {
  fedex_easy_return: "US boxless",
  international_return: "Cross border",
  other: "Domestic standard",
};

/**
 * Formats a shipping provider value for display.
 * Returns the display name if known, otherwise returns the original value.
 * Returns undefined if input is undefined/null/empty.
 */
export function formatShippingProviderDisplayName(
  shippingProvider: string | undefined | null,
): string {
  if (!shippingProvider) {
    return "unknown";
  }
  return shippingProviderDisplayNames[shippingProvider] ?? shippingProvider;
}

export interface ReturnAnalyticsTotals {
  current: ReturnAnalyticsTotalsSnapshot;
  previous: ReturnAnalyticsTotalsSnapshot;
}

export interface ReturnOperationsTotals {
  operations: Operations[];
}

export interface ReturnOperationsNewTotals {
  operations: OperationsNew[];
}

const OrderTrackingAnalyticsTotalsSnapshot_ = z.object({
  timePeriod: z.string(),
  triggerType: z.string(),
  orderId: z.string(),
  orderNumber: z.string(),
  statuses: z.array(z.string()),
  pageViews: z.number(),
  totalPrice: z.number(),
});
export const OrderTrackingAnalyticsTotalsSnapshotSchema: z.ZodType<OrderTrackingAnalyticsTotalsSnapshot> =
  OrderTrackingAnalyticsTotalsSnapshot_;
export interface OrderTrackingAnalyticsTotalsSnapshot
  extends z.infer<typeof OrderTrackingAnalyticsTotalsSnapshot_> {}

const OrderTrackingAnalyticsTotals_ = z.object({
  current: z.array(OrderTrackingAnalyticsTotalsSnapshot_),
  previous: z.array(OrderTrackingAnalyticsTotalsSnapshot_),
});
export const OrderTrackingAnalyticsTotalsSchema: z.ZodType<OrderTrackingAnalyticsTotals> =
  OrderTrackingAnalyticsTotals_;
export interface OrderTrackingAnalyticsTotals
  extends z.infer<typeof OrderTrackingAnalyticsTotals_> {}

const TrackingEmailsAndPageAnalyticsAggregates_ = z.object({
  totalEmailsDelivered: z.number(),
  totalEmailsDeliveredPrevious: z.number(),
  totalEmailsOpened: z.number(),
  totalEmailsOpenedPrevious: z.number(),
  totalEmailsClicked: z.number(),
  totalEmailsClickedPrevious: z.number(),
  reducedTotalEmailSnapshotsByTimePeriodSeries: z.array(
    z.array(z.object({ date: z.string(), value: z.number() })),
  ),
  reducedTotalEmailSnapshotsByTriggerTypeAggregated: z.record(
    z.string(),
    z.object({
      triggerType: z.string(),
      emailSends: z.number(),
      emailOpens: z.number(),
      emailClicks: z.number(),
      upsellRevenue: z.number(),
    }),
  ),
});
export const TrackingEmailsAndPageAnalyticsAggregatesSchema: z.ZodType<TrackingEmailsAndPageAnalyticsAggregates> =
  TrackingEmailsAndPageAnalyticsAggregates_;
export interface TrackingEmailsAndPageAnalyticsAggregates
  extends z.infer<typeof TrackingEmailsAndPageAnalyticsAggregates_> {}

export function isReturnDataSortKey(key: string): key is ReturnDataColumnKey {
  return (Object.values(ReturnDataColumnKey) as string[]).includes(key);
}

export interface ReturnAnalyticsTag {
  tag_name: string;
  pill_theme: string;
}

export interface ReturnDataEnumFields {
  id?: string;
  returnType?: ReturnTypeEnum;
  orderNumbers?: string[];
  newOrderNumber?: string;
  compensationMethods?: CompensationMethod[];
  status?: ReturnStatus;
  covered?: boolean;
  shopifyOrderIds?: string[];
  salesChannels?: SalesChannelValues[];
  giftcardCodes?: string[];
  tags?: ReturnAnalyticsTag[];
}

export interface ReturnDataStringFields {
  customerEmail?: string;
  currency?: Currency;
  media?: string[];
  approvedBy?: string;
  proccessedBy?: string;
  variantExchangeProductIds?: string[];
  variantExchangeTitles?: string[];
  variantExchangeVariantIds?: string[];
  exchangeForProductTitles?: string[];
  exchangeForProductIds?: string[];
  exchangeForVariantIds?: string[];
  advancedExchangeTitles?: string[];
  advancedExchangeVariantIds?: string[];
  exchangeOrderIds?: string[];
  customerComments?: string[];
  aiReturnReasonSummaries?: string[];
  referralEmail?: string;
  trackingNumbers?: string[];
  orderTrackingNumbers?: string[];
  serviceLevels?: string[];
  merchantAddressNames?: string[];
  orderCountryCodes?: string[];
  productTitles?: string[];
  variantTitles?: string[];
  productSkus?: string[];
  productStatuses?: string[];
  productReasons?: string[];
  productIds?: string[];
  variantIds?: string[];
  productRejectReasons?: string[];
  returnMethod?: string;
  shippingProvider?: string;
  dispositionMerchantNotes?: string[];
  dispositionMerchantGrades?: string[];
  dispositionMerchantOutcomes?: string[];
  customerImages?: string[];
  fulfillmentLocationIds?: string[];
  vendors?: string[];
  createdBy?: string;
  returnCreatedBy?: string;
  returnAssessments?: string;
  warrantyAssessments?: string;
  labelStatus?: string;
}

export interface ReturnDataNumberFields {
  returnValueNoTaxNoAdjustment?: number;
  returnTax?: number;
  returnAdjustment?: number;
  returnTotalValue?: number;
  returnExchangeValue?: number;
  returnStoreCreditValue?: number;
  returnRefundValue?: number;
  returnTotalBonuses?: number;
  returnTotalFees?: number;
  returnTotalAdjustments?: number;
  originalOrderItemQuantity?: number;
  returnedProductCount?: number;
  upsellAmount?: number;
  customerShippingCost?: number;
  productPrices?: number[];
  labelCost?: number;
}

export interface ReturnDataDateFields {
  orderCreatedAts?: string[];
  returnCreatedAt?: Date;
  returnProcessedAt?: Date;
  approvedAt?: Date;
  mostRecentLabelUpdate?: Date;
  returnToMerchantDeliveredDate?: Date;
}

export interface ReturnData {
  enumFields: ReturnDataEnumFields;
  stringFields: ReturnDataStringFields;
  numberFields: ReturnDataNumberFields;
  dateFields: ReturnDataDateFields;
}

export interface ReturnedProductEnumFields {
  returnType?: ReturnTypeEnum;
  orderNumber?: string;
  productName?: string;
  variantName?: string;
  sku?: string;
  compensationMethod?: CompensationMethod;
  returnId?: string;
  shopifyProductId?: string;
  shopifyVariantId?: string;
  shopifyOrderId?: string;
  status?: ReturnedProductStatus;
  returnStatus?: ReturnStatus;
  processedBy?: string | undefined;
  approvedBy?: string | undefined;
  salesChannel?: string;
  covered?: boolean;
  currency?: Currency;
}

export interface ReturnedProductStringFields {
  reason?: string;
  primaryReason?: string;
  secondaryReason?: string;
  rejectReason?: string;
  returnMethod?: ReturnMethodType;
  createdBy?: string;
  customerEmail?: string;
  dispositionMerchantNotes?: string;
  dispositionMerchantGrade?: string;
  dispositionMerchantOutcome?: string;
  customerImages?: string[];
  fulfillmentLocationId?: string;
  customerComments?: string[];
  aiReturnReasonSummary?: string;
  trackingNumbers?: string[];
  orderTrackingNumbers?: string[];
  vendor?: string;
  tags?: string[];
  orderCountryCode?: string;
  shippingProvider?: string;
  upc?: string;
  returnCreatedBy?: string;
  returnAssessments?: string;
  warrantyAssessments?: string;
  labelStatus?: string;
}

export interface ReturnedProductNumberFields {
  productValueNoTaxNoAdjustment?: number;
  productTax?: number;
  productAdjustment?: number;
  productTotalValue?: number;
  productExchangeValue?: number;
  productStoreCreditValue?: number;
  productRefundValue?: number;
  productPrice?: number;
  productQuantity?: number;
}

export interface ReturnedProductDateFields {
  created_at?: Date;
  updated_at?: Date;
  return_processed_at?: Date;
  return_approved_at?: Date;
  product_processed_at?: Date;
  product_approved_at?: Date;
  returnToMerchantDeliveredDate?: Date;
  order_created_at?: Date;
  mostRecentLabelUpdate?: Date;
}

export interface ReturnedProductData {
  stringFields: ReturnedProductStringFields;
  enumFields: ReturnedProductEnumFields;
  numberFields: ReturnedProductNumberFields;
  dateFields: ReturnedProductDateFields;
}

export function isReturnedProductSortKey(
  key: string,
): key is ReturnProductColumnKey {
  return (Object.values(ReturnProductColumnKey) as string[]).includes(key);
}

export interface OrderProductEnumFields {
  orderName?: string;
  shopifyOrderId?: string;
  lineItemId?: string;
  productId?: string;
  variantId?: string;
  productTitle?: string;
  variantTitle?: string;
  sku?: string;
  productType?: string;
  vendor?: string;
  salesChannel?: string;
  customerEmail?: string;
  customerName?: string;
  orderCountryCode?: string;
  financialStatus?: string;
  fulfillmentLocationId?: string;
  fulfillmentShipmentStatus?: string;
  currency?: Currency;
}

export interface OrderProductStringFields {
  upc?: string;
  tags?: string[];
  productTags?: string[];
  orderTrackingNumbers?: string[];
  returnIds?: string[];
  returnLineItemIds?: string[];
  compensationMethods?: string[];
  returnTypes?: string[];
  returnReasons?: string[];
  returnMethods?: string[];
  returnProductStatuses?: string[];
  exchangeTypes?: string[];
  returnToMerchantDeliveredDates?: string[];
  returnRejectReasons?: string[];
  dispositionMerchantNotes?: string[];
  dispositionMerchantGrades?: string[];
  dispositionMerchantOutcomes?: string[];
}

/** Variant exchange target — from `exchange_group_item` (camelCase JSON). */
export interface ExchangeGroupItem {
  title?: string;
  productId?: string;
  variantId?: string;
}

/** Even-exchange target — from `exchange_for` (snake_case JSON). */
export interface ExchangeForItem {
  productTitle?: string;
  productId?: string;
  variantId?: string;
}

/** One entry in the advanced-exchange selection. */
export interface AdvancedExchangeItem {
  title?: string;
  variantId?: string;
}

export interface OrderProductObjectArrayFields {
  exchangeGroupItems?: ExchangeGroupItem[];
  exchangeForItems?: ExchangeForItem[];
  advancedExchangeItems?: AdvancedExchangeItem[];
}

export interface OrderProductBooleanFields {
  isExchangeOrder?: boolean;
  isProtected?: boolean;
  isRedoEligible?: boolean;
  isPackageProtected?: boolean;
  isCustomerFirstOrder?: boolean;
  hasProductVariantExchange?: boolean;
  hasProductAdvancedExchange?: boolean;
}

export interface OrderProductNumberFields {
  quantity?: number;
  unitPrice?: number;
  price?: number;
  discountAllocations?: number;
  taxPrice?: number;
  totalQuantityReturned?: number;
  totalReturnPrice?: number;
  totalReturnRefundValue?: number;
  totalReturnStoreCreditValue?: number;
  totalReturnExchangeValue?: number;
  totalVariantExchangeValue?: number;
  totalAdvancedExchangeValue?: number;
  totalReturnTax?: number;
  totalReturnAdjustment?: number;
  totalReturnProductValue?: number;
  totalReturnValueNoTaxNoAdjustment?: number;
}

export interface OrderProductDateFields {
  orderProcessedAt?: Date;
  orderCancelledAt?: Date;
  orderProcessedDate?: Date;
  orderCancelledDate?: Date;
  orderItemDeliveredAt?: Date;
  orderItemDeliveredDate?: Date;
}

export interface OrderProductData {
  enumFields: OrderProductEnumFields;
  stringFields: OrderProductStringFields;
  booleanFields: OrderProductBooleanFields;
  numberFields: OrderProductNumberFields;
  dateFields: OrderProductDateFields;
  objectArrayFields: OrderProductObjectArrayFields;
}

export function isOrderProductSortKey(
  key: string,
): key is OrderProductColumnKey {
  return (Object.values(OrderProductColumnKey) as string[]).includes(key);
}

export interface WarrantyCustomerData {
  teamId: string;
  customerEmail: string;
  warrantyName: string;
  productName: string;
  variantName: string;
  warrantyRegistrationDate: string;
  customerPhone?: string;
  customerName?: string;
  customerAddress?: string;
  customerSecondAddress?: string;
  customerCity?: string;
  customerProvince?: string;
  customerPostal?: string;
  optedIntoMarketing?: boolean;
  source?: PurchaseChannel;
  channelName?: string;
  orderNumber?: string;
}

export function isWarrantyCustomerSortKey(
  key: string,
): key is WarrantyCustomerDataColumnKey {
  return (Object.values(WarrantyCustomerDataColumnKey) as string[]).includes(
    key,
  );
}

export interface PurchasedExtendedWarrantyData {
  orderId?: string;
  companyName?: string;
  name?: string;
  address1?: string;
  address2?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
  phoneNumber?: string;
  email?: string;
  orderDate?: Date;
  deliveryDate?: Date;
  itemPrice?: number;
  itemSku?: string;
  itemName?: string;
  termPurchased?: string;
  activationDate?: Date;
  expirationDate?: Date;
  warrantyPrice?: number;
  warrantyPurchaseDate?: Date;
  warrantyUnitPrice?: number;
  warrantyQuantity?: number;
  revSharePrice?: number;
  shopCurrency?: string;
}

export enum PurchasedExtendedWarrantyDataSortKeys {
  ORDER_ID = "orderId",
  COMPANY_NAME = "companyName",
  NAME = "name",
  ADDRESS1 = "address1",
  ADDRESS2 = "address2",
  CITY = "city",
  STATE = "state",
  COUNTRY = "country",
  ZIP = "zip",
  PHONE_NUMBER = "phoneNumber",
  EMAIL = "email",
  ORDER_DATE = "orderDate",
  DELIVERY_DATE = "deliveryDate",
  ITEM_PRICE = "itemPrice",
  ITEM_SKU = "itemSku",
  ITEM_NAME = "itemName",
  WARRANTY_PRICE = "warrantyPrice",
  WARRANTY_PURCHASE_DATE = "warrantyPurchaseDate",
  TERM_PURCHASED = "termPurchased",
  ACTIVATION_DATE = "activationDate",
  EXPIRATION_DATE = "expirationDate",
  WARRANTY_UNIT_PRICE = "warrantyUnitPrice",
  WARRANTY_QUANTITY = "warrantyQuantity",
  REV_SHARE_PRICE = "revSharePrice",
}

export function isPurchasedExtendedWarrantySortKey(
  key: string,
): key is PurchasedExtendedWarrantyDataSortKeys {
  return (
    Object.values(PurchasedExtendedWarrantyDataSortKeys) as string[]
  ).includes(key);
}

export interface OperationAnalyticsTotals {
  current: OperationsAnalyticsTotalsSnapshot;
  previous: OperationsAnalyticsTotalsSnapshot;
}

export interface OperationsData {
  fields: Operations;
}

export enum OperationDataSortKeys {
  USER_NAME = "operations:user_name",
  CREATED = "operations:created",
  APPROVED = "operations:approved",
  PROCESSED = "operations:processed",
}

export interface OperationsNew {
  email: string | null;
  userName: string | null;
  numApproved: number;
  numCreated: number;
  numProcessedComplete: number;
  numProcessedIncomplete: number;
}

export interface Operations {
  EMAIL: string;
  USER_NAME: string;
  NUM_APPROVED: number;
  NUM_CREATED: number;
  NUM_PROCESSED: number;
}

export const OperationCsvHeaders: { [key in keyof Operations]: string } = {
  EMAIL: "Email",
  USER_NAME: "User",
  NUM_CREATED: "Created",
  NUM_APPROVED: "Approved",
  NUM_PROCESSED: "Processed",
};

export interface OperationsAnalyticsTotalsSnapshot {
  operations: Operations[];
}

export type DatedReturnedList = {
  totalAmountWithReturns: number;
  totalAmount: number;
  created_at: string;
}[];

export type DatedRevenueComparisonList = {
  refundedAmount: number;
  retainedAmount: number;
  created_at: string;
}[];

export interface ReturnsByProduct {
  EXCHANGE_RATE: number;
  PRODUCT_ID: string;
  PRODUCT_TITLE: string;
  PURCHASE_COUNT: number;
  RETURN_RATE: number;
  TOTAL_EXCHANGES: number;
  TOTAL_RETURNS: number;
  index?: number;
}

export interface ReturnsByCollection {
  EXCHANGE_RATE: number;
  PURCHASE_COUNT: number;
  RETURN_RATE: number;
  TOTAL_EXCHANGES: number;
  TOTAL_RETURNS: number;
  COLLECTION: string;
  index?: number;
}

export interface ReturnsByItem {
  EXCHANGE_RATE: number;
  PURCHASE_COUNT: number;
  RETURN_RATE: number;
  TOTAL_EXCHANGES: number;
  TOTAL_RETURNS: number;
  ITEM: string;
  index?: number;
}

export type ReturnsByCollectionReason = ReturnsByCollection & {
  REASON: string;
};

export type ReturnsByItemReason = ReturnsByItem & { REASON: string };

export type ReturnsByProductReason = ReturnsByProduct & {
  REASON: string;
  TOTAL_PRODUCT_RETURNS: number;
};

export const ReturnsByProductCsvHeaders: {
  [key in keyof ReturnsByProduct]: string;
} = {
  PRODUCT_TITLE: "Product Title",
  PRODUCT_ID: "Product ID",
  PURCHASE_COUNT: "Items Ordered",
  TOTAL_RETURNS: "Items Returned",
  TOTAL_EXCHANGES: "Total Exchanges",
  RETURN_RATE: "Return Rate",
  EXCHANGE_RATE: "Exchange Rate",
};

export const ReturnsByProductReasonCsvHeaders: {
  [key in keyof ReturnsByProductReason]: string;
} = {
  PRODUCT_TITLE: "Product Title",
  PRODUCT_ID: "Product ID",
  REASON: "Return Reason",
  PURCHASE_COUNT: "Items Ordered",
  TOTAL_RETURNS: "Items Returned",
  TOTAL_EXCHANGES: "Total Exchanges",
  RETURN_RATE: "Return Rate",
  EXCHANGE_RATE: "Exchange Rate",
  TOTAL_PRODUCT_RETURNS: "Total Returns",
};

export const ReturnsByProductCollectionCsvHeaders: {
  [key in keyof ReturnsByCollection]: string;
} = {
  COLLECTION: "Collection",
  TOTAL_RETURNS: "Items Returned",
  PURCHASE_COUNT: "Items Ordered",
  TOTAL_EXCHANGES: "Total Exchanges",
  RETURN_RATE: "Return Rate",
  EXCHANGE_RATE: "Exchange Rate",
};

export const ReturnsByProductCollectionReasonCsvHeaders: {
  [key in keyof ReturnsByCollectionReason]: string;
} = {
  COLLECTION: "Collection",
  REASON: "Return Reason",
  PURCHASE_COUNT: "Items Ordered",
  TOTAL_RETURNS: "Items Returned",
  TOTAL_EXCHANGES: "Total Exchanges",
  RETURN_RATE: "Return Rate",
  EXCHANGE_RATE: "Exchange Rate",
};

export const ReturnsByProductItemCsvHeaders: {
  [key in keyof ReturnsByItem]: string;
} = {
  ITEM: "Item",
  PURCHASE_COUNT: "Items Ordered",
  TOTAL_RETURNS: "Items Returned",
  TOTAL_EXCHANGES: "Total Exchanges",
  RETURN_RATE: "Return Rate",
  EXCHANGE_RATE: "Exchange Rate",
};

export const ReturnsByProductItemReasonCsvHeaders: {
  [key in keyof ReturnsByItemReason]: string;
} = {
  ITEM: "Item",
  REASON: "Return Reason",
  PURCHASE_COUNT: "Items Ordered",
  TOTAL_RETURNS: "Items Returned",
  TOTAL_EXCHANGES: "Total Exchanges",
  RETURN_RATE: "Return Rate",
  EXCHANGE_RATE: "Exchange Rate",
};

export interface ReturnReasonsBreakdown {
  index: number;
  parentReason: string;
  reason: string;
  count: number;
  maxCount: number;
}

export interface ReturnAnalyticsTotalsSnapshot {
  range: { start: string; end: string };
  returns: {
    total: number;
    perReason: { reason: string; count: number }[];
    processed: number;
    perProduct: ReturnsByProduct[];
    perProductReason: ReturnsByProductReason[];
    perCollection: ReturnsByCollection[];
    perCollectionReason: ReturnsByCollectionReason[];
    perItem: ReturnsByItem[];
    perItemReason: ReturnsByItemReason[];
  };
  orders: { total: number; withReturns: number };
  products: { total: number; withReturns: number };
  revenue: { total: number; withReturns: number };
  datedOrders: DatedReturnedList;
  datedItems: DatedReturnedList;
  datedRevenue: DatedReturnedList;
  datedReturnedRevenue: DatedRevenueComparisonList;
}

export interface ReturnOperationsTotalsSnapshot {
  range: { start: string; end: string };
  operations: Operations[];
}

const ProblemOrdersAnalyticsSnapshot_ = z.object({
  orderNumber: z.string(),
  trackingCode: z.string(),
  shopifyOrderId: z.string(),
  message: z.string(),
  status: z.string(),
  carrier: z.string(),
  timePeriod: z.string(),
  shipDate: z.string(),
  estDeliveryDate: z.string(),
  problemStatuses: z.array(z.string()),
  shippingAddress1: z.string(),
  shippingAddress2: z.string(),
  shippingCity: z.string(),
  shippingProvince: z.string(),
  shippingZip: z.string(),
  shippingCountry: z.string(),
});
export const ProblemOrdersAnalyticsSnapshotSchema: z.ZodType<ProblemOrdersAnalyticsSnapshot> =
  ProblemOrdersAnalyticsSnapshot_;
export interface ProblemOrdersAnalyticsSnapshot
  extends z.infer<typeof ProblemOrdersAnalyticsSnapshot_> {}

const ProblemOrdersAnalyticsTotals_ = z.object({
  current: z.array(ProblemOrdersAnalyticsSnapshot_),
  previous: z.array(ProblemOrdersAnalyticsSnapshot_),
});
export const ProblemOrdersAnalyticsTotalsSchema: z.ZodType<ProblemOrdersAnalyticsTotals> =
  ProblemOrdersAnalyticsTotals_;
export interface ProblemOrdersAnalyticsTotals
  extends z.infer<typeof ProblemOrdersAnalyticsTotals_> {}

export interface ProblemOrderShipmentMethod {
  carrier: string;
  logoUrl: string;
  totalOrders: number;
  totalProblems: number;
  problemTypes: string[];
}

const DiscountCode_ = z.object({
  amount: z.coerce.number(),
  code: z.string(),
  type: z.string(),
});
export const DiscountCodeSchema: z.ZodType<DiscountCode> = DiscountCode_;
export interface DiscountCode extends z.infer<typeof DiscountCode_> {}

const UpsellAnalyticsTotalsSnapshot_ = z.object({
  timePeriod: z.string(),
  daysAfter: z.number(),
  upsellRevenue: z.number(),
  upsoldOrderNumber: z.string(),
  originalOrderNumber: z.string(),
  pageViews: z.number(),
  pageClicks: z.number(),
  emailViews: z.number(),
  emailClicks: z.number(),
  link: z.string(),
  discountCodes: z.array(DiscountCode_),
});
export const UpsellAnalyticsTotalsSnapshotSchema: z.ZodType<UpsellAnalyticsTotalsSnapshot> =
  UpsellAnalyticsTotalsSnapshot_;
export interface UpsellAnalyticsTotalsSnapshot
  extends z.infer<typeof UpsellAnalyticsTotalsSnapshot_> {}

const UpsellAnalyticsTotals_ = z.object({
  current: z.array(UpsellAnalyticsTotalsSnapshot_),
  previous: z.array(UpsellAnalyticsTotalsSnapshot_),
});
export const UpsellAnalyticsTotalsSchema: z.ZodType<UpsellAnalyticsTotals> =
  UpsellAnalyticsTotals_;
export interface UpsellAnalyticsTotals
  extends z.infer<typeof UpsellAnalyticsTotals_> {}

const AllEventsAnalyticsTotalsSnapshot_ = z.object({
  timePeriod: z.string(),
  orderNumber: z.string(),
  emailViews: z.number(),
  emailClicks: z.number(),
  pageViews: z.number(),
  pageClicks: z.number(),
});
export const AllEventsAnalyticsTotalsSnapshotSchema: z.ZodType<AllEventsAnalyticsTotalsSnapshot> =
  AllEventsAnalyticsTotalsSnapshot_;
export interface AllEventsAnalyticsTotalsSnapshot
  extends z.infer<typeof AllEventsAnalyticsTotalsSnapshot_> {}

const AllEventsAnalyticsTotals_ = z.object({
  current: z.array(AllEventsAnalyticsTotalsSnapshot_),
  previous: z.array(AllEventsAnalyticsTotalsSnapshot_),
});
export const AllEventsAnalyticsTotalsSchema: z.ZodType<AllEventsAnalyticsTotals> =
  AllEventsAnalyticsTotals_;
export interface AllEventsAnalyticsTotals
  extends z.infer<typeof AllEventsAnalyticsTotals_> {}

export interface DeliveryAnalyticsAggregatesTotals {
  current: DeliveryAnalyticsTotalsSnapshot[];
  previous: DeliveryAnalyticsTotalsSnapshot[];
}

const DeliveryAnalyticsTotalsSnapshot_ = z.object({
  timePeriod: z.string(),
  orderNumber: z.string(),
  carrier: z.string(),
  trackingCode: z.string(),
  inTransitDate: z.string(),
  deliveredDate: z.string(),
  locationId: z.string(),
  status: z.string(),
  state: z.string(),
});
export const DeliveryAnalyticsTotalsSnapshotSchema: z.ZodType<DeliveryAnalyticsTotalsSnapshot> =
  DeliveryAnalyticsTotalsSnapshot_;
export interface DeliveryAnalyticsTotalsSnapshot
  extends z.infer<typeof DeliveryAnalyticsTotalsSnapshot_> {}

const DeliveryAnalyticsTotals_ = z.object({
  current: z.array(DeliveryAnalyticsTotalsSnapshot_),
  totalCount: z.number().optional(),
});
export const DeliveryAnalyticsTotalsSchema: z.ZodType<DeliveryAnalyticsTotals> =
  DeliveryAnalyticsTotals_;
export interface DeliveryAnalyticsTotals
  extends z.infer<typeof DeliveryAnalyticsTotals_> {}

const AggregatedDeliveryAnalytics_ = z.record(
  z.string(),
  DeliveryAnalyticsTotalsSnapshot_.extend({
    totalFulfillmentTime: z.number(),
    totalDeliveryTime: z.number(),
    averageFulfillmentTime: z.number(),
    averageDeliveryTime: z.number(),
    fulfilledCount: z.number(),
    deliveredCount: z.number(),
  }),
);
export const AggregatedDeliveryAnalyticsSchema: z.ZodType<AggregatedDeliveryAnalytics> =
  AggregatedDeliveryAnalytics_;
export interface AggregatedDeliveryAnalytics
  extends z.infer<typeof AggregatedDeliveryAnalytics_> {}

const AggregatedDeliveryData_ = z.object({
  reducedDeliveryAnalyticsByAverageTimes: AggregatedDeliveryAnalytics_,
  reducedPreviousDeliveryAnalyticsByAverageTimes: AggregatedDeliveryAnalytics_,
  averageFulfillmentTime: z.number(),
  averageDeliveryTime: z.number(),
  previousAverageFulfillmentTime: z.number(),
  previousAverageDeliveryTime: z.number(),
});
export const AggregatedDeliveryDataSchema: z.ZodType<AggregatedDeliveryData> =
  AggregatedDeliveryData_;
export interface AggregatedDeliveryData
  extends z.infer<typeof AggregatedDeliveryData_> {}

const DeliveryAnalyticsAggregates_ = z.object({
  currentInTransitCount: z.number(),
  previousTotalOrders: z.number(),
  currentTotalOrders: z.number(),
  deliveryAnalyticsByTimePeriod: AggregatedDeliveryData_,
  deliveryAnalyticsByLocation: AggregatedDeliveryData_,
  deliveryAnalyticsByState: AggregatedDeliveryData_,
});
export const DeliveryAnalyticsAggregatesSchema: z.ZodType<DeliveryAnalyticsAggregates> =
  DeliveryAnalyticsAggregates_;
export interface DeliveryAnalyticsAggregates
  extends z.infer<typeof DeliveryAnalyticsAggregates_> {}

export interface EmailsByTriggerAnalyticsTotals {
  current: EmailsByTriggerAnalyticsTotalsSnapshot[];
}

export interface EmailsByTriggerAnalyticsTotalsSnapshot {
  triggerType: string;
  emailSends: number;
  emailOpens: number;
  emailClicks: number;
  upsellRevenue: number;
}

function dateStringHasTimezone(date: string): boolean {
  // Match common timezone formats:
  // Z (UTC)
  // +/-HH:MM
  // +/-HHMM
  // +/-HH
  // GMT/UTC +/-HH:MM
  // GMT/UTC +/-HHMM
  // GMT/UTC +/-HH
  const timezoneRegex =
    /(?:Z|[+-]\d{2}(?::\d{2})?|GMT|UTC(?:\s*[+-]\d{2}(?::\d{2})?)?)/i;
  return timezoneRegex.test(date);
}

function formatDateAccordingToTimezone(date: string | Date, timezone: string) {
  const dateString = date instanceof Date ? date.toISOString() : date;
  if (!dateString) {
    return "";
  }
  if (dateStringHasTimezone(dateString)) {
    return new Date(dateString).toLocaleString("en-US", { timeZone: timezone });
  }
  return new Date(dateString + "z").toLocaleString("en-US", {
    timeZone: timezone,
  });
}

function attemptToFormatCurrency(value?: number, currency?: string): string {
  return currency && value
    ? getCurrencyFormatter(currency).format(value)
    : (value?.toFixed(2) ?? "");
}

export const getReturnDataCSVColumns = (
  timezone: string,
): {
  [K in ReturnDataColumnKey]?: {
    header: string;
    cell: (item: ReturnData) => string;
  };
} => {
  return {
    [ReturnDataColumnKey.ORDER_NUMBER]: {
      header: "Order Number(s)",
      cell: (item: ReturnData) => item.enumFields.orderNumbers?.join(";") ?? "",
    },
    [ReturnDataColumnKey.SHOPIFY_ORDER_ID]: {
      header: "Provider Order ID(s)",
      cell: (item) => item.enumFields.shopifyOrderIds?.join(";") ?? "",
    },
    [ReturnDataColumnKey.ORDER_CREATED_AT]: {
      header: "Order(s) Created At",
      cell: (item) =>
        item.dateFields.orderCreatedAts
          ? item.dateFields.orderCreatedAts
              .map((createdAt) =>
                formatDateAccordingToTimezone(createdAt, timezone),
              )
              .join(";")
          : "",
    },
    [ReturnDataColumnKey.RETURN_ID]: {
      header: "Return ID",
      cell: (item) => item.enumFields.id ?? "",
    },
    [ReturnDataColumnKey.RETURN_CREATED_AT]: {
      header: "Return Created Date",
      cell: (item) =>
        item.dateFields.returnCreatedAt
          ? formatDateAccordingToTimezone(
              item.dateFields.returnCreatedAt,
              timezone,
            )
          : "",
    },
    [ReturnDataColumnKey.RETURN_TYPE]: {
      header: "Return Type",
      cell: (item) => item.enumFields.returnType ?? "",
    },
    [ReturnDataColumnKey.RETURN_STATUS]: {
      header: "Return Status",
      cell: (item) => item.enumFields.status ?? "",
    },
    [ReturnDataColumnKey.RETURN_PROCESSED_AT]: {
      header: "Return Processed Date",
      cell: (item) =>
        item.dateFields.returnProcessedAt
          ? formatDateAccordingToTimezone(
              item.dateFields.returnProcessedAt,
              timezone,
            )
          : "",
    },
    [ReturnDataColumnKey.COMPENSATION_METHODS]: {
      header: "Return Methods",
      cell: (item) => item.enumFields.compensationMethods?.join(" + ") ?? "",
    },
    [ReturnDataColumnKey.RETURN_VALUE_NO_TAX_NO_ADJUSTMENT]: {
      header: "Return Value",
      cell: (item) =>
        item.numberFields.returnValueNoTaxNoAdjustment !== undefined
          ? getCurrencyFormatter(item.stringFields.currency).format(
              item.numberFields.returnValueNoTaxNoAdjustment,
            )
          : "",
    },
    [ReturnDataColumnKey.RETURN_TAX]: {
      header: "Return Tax",
      cell: (item) =>
        item.numberFields.returnTax !== undefined
          ? getCurrencyFormatter(item.stringFields.currency).format(
              item.numberFields.returnTax,
            )
          : "",
    },
    [ReturnDataColumnKey.RETURN_ADJUSTMENT]: {
      header: "Bonus/Fee",
      cell: (item) =>
        item.numberFields.returnAdjustment !== undefined
          ? getCurrencyFormatter(item.stringFields.currency).format(
              item.numberFields.returnAdjustment,
            )
          : "",
    },
    [ReturnDataColumnKey.RETURN_TOTAL_VALUE]: {
      header: "Return Value Total",
      cell: (item) =>
        item.numberFields.returnTotalValue !== undefined
          ? getCurrencyFormatter(item.stringFields.currency).format(
              item.numberFields.returnTotalValue,
            )
          : "",
    },
    [ReturnDataColumnKey.RETURN_EXCHANGE_VALUE]: {
      header: "Exchange",
      cell: (item) =>
        item.numberFields.returnExchangeValue !== undefined
          ? getCurrencyFormatter(item.stringFields.currency).format(
              item.numberFields.returnExchangeValue,
            )
          : "",
    },
    [ReturnDataColumnKey.RETURN_STORE_CREDIT_VALUE]: {
      header: "Store Credit",
      cell: (item) =>
        item.numberFields.returnStoreCreditValue !== undefined
          ? getCurrencyFormatter(item.stringFields.currency).format(
              item.numberFields.returnStoreCreditValue,
            )
          : "",
    },
    [ReturnDataColumnKey.RETURN_REFUND_VALUE]: {
      header: "Refund",
      cell: (item) =>
        item.numberFields.returnRefundValue !== undefined
          ? getCurrencyFormatter(item.stringFields.currency).format(
              item.numberFields.returnRefundValue,
            )
          : "",
    },
    [ReturnDataColumnKey.MEDIA]: {
      header: "Media",
      cell: (item) => item.stringFields.media?.join(" ") ?? "",
    },
    [ReturnDataColumnKey.APPROVEDAT]: {
      header: "Approved At",
      cell: (item) =>
        item.dateFields.approvedAt
          ? formatDateAccordingToTimezone(item.dateFields.approvedAt, timezone)
          : "",
    },
    [ReturnDataColumnKey.APPROVEDBY]: {
      header: "Approved By",
      cell: (item) => item.stringFields.approvedBy ?? "",
    },
    [ReturnDataColumnKey.PROCESSEDBY]: {
      header: "Processed By",
      cell: (item) => item.stringFields.proccessedBy ?? "",
    },
    [ReturnDataColumnKey.SALES_CHANNEL]: {
      header: "Sales Channel(s)",
      cell: (item) => item.enumFields.salesChannels?.join(";") ?? "",
    },
    [ReturnDataColumnKey.GIFTCARD_CODES]: {
      header: "Gift Card Codes",
      cell: (item) => item.enumFields.giftcardCodes?.join(";") ?? "",
    },
    [ReturnDataColumnKey.TAGS]: {
      header: "Tags",
      cell: (item) =>
        item.enumFields.tags?.map((tag) => tag.tag_name).join(";") ?? "",
    },
    [ReturnDataColumnKey.VARIANT_EXCHANGE_PRODUCT_IDS]: {
      header: "Variant Exchange Product IDs",
      cell: (item) =>
        item.stringFields.variantExchangeProductIds?.join(";") ?? "",
    },
    [ReturnDataColumnKey.VARIANT_EXCHANGE_TITLES]: {
      header: "Variant Exchange Titles",
      cell: (item) => item.stringFields.variantExchangeTitles?.join(";") ?? "",
    },
    [ReturnDataColumnKey.VARIANT_EXCHANGE_VARIANT_IDS]: {
      header: "Variant Exchange Variant IDs",
      cell: (item) =>
        item.stringFields.variantExchangeVariantIds?.join(";") ?? "",
    },
    [ReturnDataColumnKey.EXCHANGE_FOR_PRODUCT_TITLES]: {
      header: "Exchange For Product Titles",
      cell: (item) =>
        item.stringFields.exchangeForProductTitles?.join(";") ?? "",
    },
    [ReturnDataColumnKey.EXCHANGE_FOR_PRODUCT_IDS]: {
      header: "Exchange For Product IDs",
      cell: (item) => item.stringFields.exchangeForProductIds?.join(";") ?? "",
    },
    [ReturnDataColumnKey.EXCHANGE_FOR_VARIANT_IDS]: {
      header: "Exchange For Variant IDs",
      cell: (item) => item.stringFields.exchangeForVariantIds?.join(";") ?? "",
    },
    [ReturnDataColumnKey.RETURNED_PRODUCT_COUNT]: {
      header: "Returned Product Count",
      cell: (item) => item.numberFields.returnedProductCount?.toFixed(0) ?? "",
    },
    [ReturnDataColumnKey.EXCHANGE_ORDER_IDS]: {
      header: "Exchange Order IDs",
      cell: (item) => item.stringFields.exchangeOrderIds?.join(";") ?? "",
    },
    [ReturnDataColumnKey.ORIGINAL_ORDER_ITEM_QUANTITY]: {
      header: "Original Order Item Quantity",
      cell: (item) =>
        item.numberFields.originalOrderItemQuantity?.toFixed(0) ?? "",
    },
    [ReturnDataColumnKey.ADVANCED_EXCHANGE_TITLES]: {
      header: "Advanced Exchange Titles",
      cell: (item) => item.stringFields.advancedExchangeTitles?.join(";") ?? "",
    },
    [ReturnDataColumnKey.ADVANCED_EXCHANGE_VARIANT_IDS]: {
      header: "Advanced Exchange Titles",
      cell: (item) =>
        item.stringFields.advancedExchangeVariantIds?.join(";") ?? "",
    },
    [ReturnDataColumnKey.CUSTOMER_COMMENTS]: {
      header: "Customer Comments",
      cell: (item) => item.stringFields.customerComments?.join(";") ?? "",
    },
    [ReturnDataColumnKey.AI_RETURN_REASON_SUMMARIES]: {
      header: "AI Return Reason Summaries",
      cell: (item) =>
        item.stringFields.aiReturnReasonSummaries?.join(";") ?? "",
    },
    [ReturnDataColumnKey.REFERRAL_EMAIL]: {
      header: "Referral Email",
      cell: (item) => item.stringFields.referralEmail ?? "",
    },
    [ReturnDataColumnKey.RETURN_TRACKING_NUMBERS]: {
      header: "Return Tracking Numbers",
      cell: (item) => item.stringFields.trackingNumbers?.join(";") ?? "",
    },
    [ReturnDataColumnKey.ORDER_TRACKING_NUMBERS]: {
      header: "Order Tracking Numbers",
      cell: (item) => item.stringFields.orderTrackingNumbers?.join(";") ?? "",
    },
    [ReturnDataColumnKey.SERVICE_LEVELS]: {
      header: "Service Levels",
      cell: (item) => item.stringFields.serviceLevels?.join(";") ?? "",
    },
    [ReturnDataColumnKey.MERCHANT_ADDRESS_NAMES]: {
      header: "Merchant Address Names",
      cell: (item) => item.stringFields.merchantAddressNames?.join(";") ?? "",
    },
    [ReturnDataColumnKey.MOST_RECENT_LABEL_UPDATE]: {
      header: "Most Recent Label Update",
      cell: (item) =>
        item.dateFields.mostRecentLabelUpdate
          ? formatDateAccordingToTimezone(
              item.dateFields.mostRecentLabelUpdate,
              timezone,
            )
          : "",
    },
    [ReturnDataColumnKey.LABEL_STATUS]: {
      header: "Label Status",
      cell: (item) => item.stringFields.labelStatus ?? "",
    },
    [ReturnDataColumnKey.LABEL_COST]: {
      header: "Label Cost",
      cell: (item) =>
        item.numberFields.labelCost !== undefined
          ? getCurrencyFormatter(item.stringFields.currency).format(
              item.numberFields.labelCost,
            )
          : "",
    },
    [ReturnDataColumnKey.UPSELL_AMOUNT]: {
      header: "Upsell Amount",
      cell: (item) =>
        item.numberFields.upsellAmount !== undefined
          ? getCurrencyFormatter(item.stringFields.currency).format(
              item.numberFields.upsellAmount,
            )
          : "",
    },
    [ReturnDataColumnKey.CUSTOMER_SHIPPING_COST]: {
      header: "Customer Shipping Cost",
      cell: (item) =>
        item.numberFields.customerShippingCost !== undefined
          ? getCurrencyFormatter(item.stringFields.currency).format(
              item.numberFields.customerShippingCost,
            )
          : "",
    },
    [ReturnDataColumnKey.RETURN_TOTAL_BONUSES]: {
      header: "Return Total Bonuses",
      cell: (item) =>
        item.numberFields.returnTotalBonuses !== undefined
          ? getCurrencyFormatter(item.stringFields.currency).format(
              item.numberFields.returnTotalBonuses,
            )
          : "",
    },
    [ReturnDataColumnKey.RETURN_TOTAL_FEES]: {
      header: "Return Total Fees",
      cell: (item) =>
        item.numberFields.returnTotalFees !== undefined
          ? getCurrencyFormatter(item.stringFields.currency).format(
              item.numberFields.returnTotalFees,
            )
          : "",
    },
    [ReturnDataColumnKey.RETURN_TOTAL_ADJUSTMENTS]: {
      header: "Return Total Adjustments",
      cell: (item) =>
        item.numberFields.returnTotalAdjustments !== undefined
          ? getCurrencyFormatter(item.stringFields.currency).format(
              item.numberFields.returnTotalAdjustments,
            )
          : "",
    },
    [ReturnDataColumnKey.ORDER_COUNTRY_CODE]: {
      header: "Order Country Code(s)",
      cell: (item) => item.stringFields.orderCountryCodes?.join(";") ?? "",
    },
    [ReturnDataColumnKey.RETURN_TO_MERCHANT_DELIVERED_DATE]: {
      header: "Return To Merchant Delivered Date",
      cell: (item) =>
        item.dateFields.returnToMerchantDeliveredDate
          ? formatDateAccordingToTimezone(
              item.dateFields.returnToMerchantDeliveredDate,
              timezone,
            )
          : "",
    },
    [ReturnDataColumnKey.PRODUCT_TITLES]: {
      header: "Product Titles",
      cell: (item) => item.stringFields.productTitles?.join(";") ?? "",
    },
    [ReturnDataColumnKey.VARIANT_TITLES]: {
      header: "Variant Titles",
      cell: (item) => item.stringFields.variantTitles?.join(";") ?? "",
    },
    [ReturnDataColumnKey.PRODUCT_SKUS]: {
      header: "Product SKUs",
      cell: (item) => item.stringFields.productSkus?.join(";") ?? "",
    },
    [ReturnDataColumnKey.PRODUCT_STATUSES]: {
      header: "Product Statuses",
      cell: (item) => item.stringFields.productStatuses?.join(";") ?? "",
    },
    [ReturnDataColumnKey.PRODUCT_REASONS]: {
      header: "Product Reasons",
      cell: (item) => item.stringFields.productReasons?.join(";") ?? "",
    },
    [ReturnDataColumnKey.PRODUCT_IDS]: {
      header: "Product IDs",
      cell: (item) => item.stringFields.productIds?.join(";") ?? "",
    },
    [ReturnDataColumnKey.VARIANT_IDS]: {
      header: "Variant IDs",
      cell: (item) => item.stringFields.variantIds?.join(";") ?? "",
    },
    [ReturnDataColumnKey.PRODUCT_REJECT_REASONS]: {
      header: "Product Reject Reasons",
      cell: (item) => item.stringFields.productRejectReasons?.join(";") ?? "",
    },
    [ReturnDataColumnKey.RETURN_METHOD]: {
      header: "Return Method",
      cell: (item) => item.stringFields.returnMethod ?? "",
    },
    [ReturnDataColumnKey.SHIPPING_METHOD]: {
      header: "Shipping Method",
      cell: (item) =>
        formatShippingProviderDisplayName(item.stringFields.shippingProvider),
    },
    [ReturnDataColumnKey.PRODUCT_PRICES]: {
      header: "Product Prices",
      cell: (item) =>
        item.numberFields.productPrices
          ? item.numberFields.productPrices
              .map((p) =>
                getCurrencyFormatter(item.stringFields.currency).format(p),
              )
              .join(";")
          : "",
    },
    [ReturnDataColumnKey.COVERED]: {
      header: "Covered",
      cell: (item) => item.enumFields.covered?.toString() ?? "",
    },
    [ReturnDataColumnKey.CUSTOMER_EMAIL]: {
      header: "Customer Email",
      cell: (item) => item.stringFields.customerEmail ?? "",
    },
    [ReturnDataColumnKey.DISPOSITION_MERCHANT_NOTES]: {
      header: "Disposition Merchant Notes",
      cell: (item) =>
        item.stringFields.dispositionMerchantNotes?.join(";") ?? "",
    },
    [ReturnDataColumnKey.DISPOSITION_MERCHANT_GRADES]: {
      header: "Disposition Merchant Grades",
      cell: (item) =>
        item.stringFields.dispositionMerchantGrades?.join(";") ?? "",
    },
    [ReturnDataColumnKey.DISPOSITION_MERCHANT_OUTCOMES]: {
      header: "Disposition Merchant Outcomes",
      cell: (item) =>
        item.stringFields.dispositionMerchantOutcomes?.join(";") ?? "",
    },
    [ReturnDataColumnKey.FULFILLMENT_LOCATION_IDS]: {
      header: "Fulfillment Location IDs",
      cell: (item) => item.stringFields.fulfillmentLocationIds?.join(";") ?? "",
    },
    [ReturnDataColumnKey.VENDORS]: {
      header: "Vendors",
      cell: (item) => item.stringFields.vendors?.join(";") ?? "",
    },
    [ReturnDataColumnKey.CREATED_BY]: {
      header: "Created By",
      cell: (item) => item.stringFields.createdBy ?? "",
    },
    [ReturnDataColumnKey.RETURN_CREATED_BY]: {
      header: "Return Created By",
      cell: (item) => item.stringFields.returnCreatedBy ?? "",
    },
    [ReturnDataColumnKey.RETURN_ASSESSMENTS]: {
      header: "Return Assessments",
      cell: (item) => item.stringFields.returnAssessments ?? "",
    },
    [ReturnDataColumnKey.WARRANTY_ASSESSMENTS]: {
      header: "Warranty Assessments",
      cell: (item) => item.stringFields.warrantyAssessments ?? "",
    },
  };
};

export const getReturnedProductCSVColumns = (
  timezone: string,
): {
  [K in ReturnProductColumnKey]?: {
    header: string;
    cell: (item: ReturnedProductData) => string;
  };
} => {
  return {
    [ReturnProductColumnKey.ORDER_NUMBER]: {
      header: "Order",
      cell: (item) => item.enumFields.orderNumber ?? "",
    },
    [ReturnProductColumnKey.ORDER_CREATED_AT]: {
      header: "Order Created Date",
      cell: (item) =>
        item.dateFields.order_created_at
          ? formatDateAccordingToTimezone(
              item.dateFields.order_created_at,
              timezone,
            )
          : "",
    },
    [ReturnProductColumnKey.SHOPIFY_ORDER_ID]: {
      header: "Provider Order ID",
      cell: (item) => item.enumFields.shopifyOrderId ?? "",
    },
    [ReturnProductColumnKey.RETURN_ID]: {
      header: "Return ID",
      cell: (item) => item.enumFields.returnId ?? "",
    },
    [ReturnProductColumnKey.PRODUCT_STATUS]: {
      header: "Product Status",
      cell: (item) => item.enumFields.status ?? "",
    },
    [ReturnProductColumnKey.RETURN_STATUS]: {
      header: "Return Status",
      cell: (item) => item.enumFields.returnStatus ?? "",
    },
    [ReturnProductColumnKey.CREATED_AT]: {
      header: "Return Created Date",
      cell: (item) =>
        item.dateFields.created_at
          ? formatDateAccordingToTimezone(item.dateFields.created_at, timezone)
          : "",
    },
    [ReturnProductColumnKey.RETURN_TYPE]: {
      header: "Return Type",
      cell: (item) => item.enumFields.returnType ?? "",
    },
    [ReturnProductColumnKey.PRODUCT_NAME]: {
      header: "Product Name",
      cell: (item) => item.enumFields.productName ?? "",
    },
    [ReturnProductColumnKey.SHOPIFY_PRODUCT_ID]: {
      header: "Shopify Product ID",
      cell: (item) => item.enumFields.shopifyProductId ?? "",
    },
    [ReturnProductColumnKey.VARIANT_NAME]: {
      header: "Variant",
      cell: (item) => item.enumFields.variantName ?? "",
    },
    [ReturnProductColumnKey.SHOPIFY_VARIANT_ID]: {
      header: "Shopify Variant ID",
      cell: (item) => item.enumFields.shopifyVariantId ?? "",
    },
    [ReturnProductColumnKey.SKU]: {
      header: "Sku",
      cell: (item) => item.enumFields.sku ?? "",
    },
    [ReturnProductColumnKey.UPC]: {
      header: "UPC",
      cell: (item) => item.stringFields.upc ?? "",
    },
    [ReturnProductColumnKey.REASON]: {
      header: "Return Reason",
      cell: (item) => item.stringFields.reason ?? "",
    },
    [ReturnProductColumnKey.PRIMARY_REASON]: {
      header: "Primary Reason",
      cell: (item) => item.stringFields.primaryReason ?? "",
    },
    [ReturnProductColumnKey.SECONDARY_REASON]: {
      header: "Secondary Reason",
      cell: (item) => item.stringFields.secondaryReason ?? "",
    },
    [ReturnProductColumnKey.COMPENSATION_METHOD]: {
      header: "Return Type",
      cell: (item) => item.enumFields.compensationMethod ?? "",
    },
    [ReturnProductColumnKey.PRODUCT_VALUE_NO_TAX_NO_ADJUSTMENT]: {
      header: "Return Value",
      cell: (item) =>
        item.numberFields.productValueNoTaxNoAdjustment !== undefined
          ? getCurrencyFormatter(item.enumFields.currency).format(
              item.numberFields.productValueNoTaxNoAdjustment,
            )
          : "",
    },
    [ReturnProductColumnKey.PRODUCT_TAX]: {
      header: "Return Tax",
      cell: (item) =>
        item.numberFields.productTax !== undefined
          ? getCurrencyFormatter(item.enumFields.currency).format(
              item.numberFields.productTax,
            )
          : "",
    },
    [ReturnProductColumnKey.PRODUCT_ADJUSTMENT]: {
      header: "Bonus/Fee",
      cell: (item) =>
        item.numberFields.productAdjustment !== undefined
          ? getCurrencyFormatter(item.enumFields.currency).format(
              item.numberFields.productAdjustment,
            )
          : "",
    },
    [ReturnProductColumnKey.PRODUCT_TOTAL_VALUE]: {
      header: "Return Value Total",
      cell: (item) =>
        item.numberFields.productTotalValue !== undefined
          ? getCurrencyFormatter(item.enumFields.currency).format(
              item.numberFields.productTotalValue,
            )
          : "",
    },
    [ReturnProductColumnKey.PRODUCT_EXCHANGE_VALUE]: {
      header: "Exchange",
      cell: (item) =>
        item.numberFields.productExchangeValue !== undefined
          ? getCurrencyFormatter(item.enumFields.currency).format(
              item.numberFields.productExchangeValue,
            )
          : "",
    },
    [ReturnProductColumnKey.PRODUCT_STORE_CREDIT_VALUE]: {
      header: "Store Credit",
      cell: (item) =>
        item.numberFields.productStoreCreditValue !== undefined
          ? getCurrencyFormatter(item.enumFields.currency).format(
              item.numberFields.productStoreCreditValue,
            )
          : "",
    },
    [ReturnProductColumnKey.PRODUCT_REFUND_VALUE]: {
      header: "Refund",
      cell: (item) =>
        item.numberFields.productRefundValue !== undefined
          ? getCurrencyFormatter(item.enumFields.currency).format(
              item.numberFields.productRefundValue,
            )
          : "",
    },
    [ReturnProductColumnKey.REJECT_REASON]: {
      header: "Reject Reason",
      cell: (item) => item.stringFields.rejectReason ?? "",
    },
    [ReturnProductColumnKey.PROCESSED_BY]: {
      header: "Processed By",
      cell: (item) => item.enumFields.processedBy ?? "",
    },
    [ReturnProductColumnKey.RETURN_PROCESSED_AT]: {
      header: "Return Processed At",
      cell: (item) =>
        item.dateFields.return_processed_at
          ? formatDateAccordingToTimezone(
              item.dateFields.return_processed_at,
              timezone,
            )
          : "",
    },
    [ReturnProductColumnKey.APPROVED_BY]: {
      header: "Approved By",
      cell: (item) => item.enumFields.approvedBy ?? "",
    },
    [ReturnProductColumnKey.RETURN_APPROVED_AT]: {
      header: "Return Approved At",
      cell: (item) =>
        item.dateFields.return_approved_at
          ? formatDateAccordingToTimezone(
              item.dateFields.return_approved_at,
              timezone,
            )
          : "",
    },
    [ReturnProductColumnKey.PRODUCT_PROCESSED_AT]: {
      header: "Product Processed At",
      cell: (item) =>
        item.dateFields.product_processed_at
          ? formatDateAccordingToTimezone(
              item.dateFields.product_processed_at,
              timezone,
            )
          : "",
    },
    [ReturnProductColumnKey.PRODUCT_APPROVED_AT]: {
      header: "Product Approved At",
      cell: (item) =>
        item.dateFields.product_approved_at
          ? formatDateAccordingToTimezone(
              item.dateFields.product_approved_at,
              timezone,
            )
          : "",
    },
    [ReturnProductColumnKey.CREATED_BY]: {
      header: "Created By",
      cell: (item) => item.stringFields.createdBy ?? "",
    },
    [ReturnProductColumnKey.RETURN_METHOD]: {
      header: "Return Method",
      cell: (item) =>
        item.stringFields.returnMethod === ReturnMethodType.ERROR
          ? ReturnMethodType.SHIPMENT
          : (item.stringFields.returnMethod ?? ""),
    },
    [ReturnProductColumnKey.SHIPPING_METHOD]: {
      header: "Shipping Method",
      cell: (item) =>
        formatShippingProviderDisplayName(item.stringFields.shippingProvider),
    },
    [ReturnProductColumnKey.QUANTITY]: {
      header: "Quantity",
      cell: (item) =>
        item.numberFields.productQuantity !== undefined
          ? item.numberFields.productQuantity.toString()
          : "",
    },
    [ReturnProductColumnKey.PRICE]: {
      header: "Price",
      cell: (item) =>
        item.numberFields.productPrice !== undefined
          ? getCurrencyFormatter(item.enumFields.currency).format(
              item.numberFields.productPrice,
            )
          : "",
    },
    [ReturnProductColumnKey.SALES_CHANNEL]: {
      header: "Sales Channel",
      cell: (item) => item.enumFields.salesChannel ?? "",
    },
    [ReturnProductColumnKey.COVERED]: {
      header: "Covered",
      cell: (item) => item.enumFields.covered?.toString() ?? "",
    },
    [ReturnProductColumnKey.CUSTOMER_EMAIL]: {
      header: "Customer Email",
      cell: (item) => item.stringFields.customerEmail ?? "",
    },
    [ReturnProductColumnKey.CUSTOMER_IMAGES]: {
      header: "Customer Images",
      cell: (item) => item.stringFields.customerImages?.join(";") ?? "",
    },
    [ReturnProductColumnKey.DISPOSITION_MERCHANT_GRADE]: {
      header: "Merchant Grade",
      cell: (item) => item.stringFields.dispositionMerchantGrade ?? "",
    },
    [ReturnProductColumnKey.DISPOSITION_MERCHANT_OUTCOME]: {
      header: "Merchant Outcome",
      cell: (item) => item.stringFields.dispositionMerchantOutcome ?? "",
    },
    [ReturnProductColumnKey.DISPOSITION_MERCHANT_NOTES]: {
      header: "Merchant Notes",
      cell: (item) => item.stringFields.dispositionMerchantNotes ?? "",
    },
    [ReturnProductColumnKey.FULFILLMENT_LOCATION_ID]: {
      header: "Fulfillment Location ID",
      cell: (item) => item.stringFields.fulfillmentLocationId ?? "",
    },
    [ReturnProductColumnKey.RETURN_TO_MERCHANT_DELIVERED_DATE]: {
      header: "Return To Merchant Delivered Date",
      cell: (item) =>
        item.dateFields.returnToMerchantDeliveredDate
          ? formatDateAccordingToTimezone(
              item.dateFields.returnToMerchantDeliveredDate,
              timezone,
            )
          : "",
    },
    [ReturnProductColumnKey.CUSTOMER_COMMENTS]: {
      header: "Customer Comments",
      cell: (item) => item.stringFields.customerComments?.join(";") ?? "",
    },
    [ReturnProductColumnKey.AI_RETURN_REASON_SUMMARY]: {
      header: "AI Return Reason Summary",
      cell: (item) => item.stringFields.aiReturnReasonSummary ?? "",
    },
    [ReturnProductColumnKey.RETURN_TRACKING_NUMBERS]: {
      header: "Return Tracking Numbers",
      cell: (item) => item.stringFields.trackingNumbers?.join(";") ?? "",
    },
    [ReturnProductColumnKey.ORDER_TRACKING_NUMBERS]: {
      header: "Order Tracking Numbers",
      cell: (item) => item.stringFields.orderTrackingNumbers?.join(";") ?? "",
    },
    [ReturnProductColumnKey.VENDOR]: {
      header: "Vendor",
      cell: (item) => item.stringFields.vendor ?? "",
    },
    [ReturnProductColumnKey.TAGS]: {
      header: "Tags",
      cell: (item) => item.stringFields.tags?.join(";") ?? "",
    },
    [ReturnProductColumnKey.ORDER_COUNTRY_CODE]: {
      header: "Order Country Code",
      cell: (item) => item.stringFields.orderCountryCode ?? "",
    },
    [ReturnProductColumnKey.RETURN_CREATED_BY]: {
      header: "Return Created By",
      cell: (item) => item.stringFields.returnCreatedBy ?? "",
    },
    [ReturnProductColumnKey.RETURN_ASSESSMENTS]: {
      header: "Return Assessments",
      cell: (item) => item.stringFields.returnAssessments ?? "",
    },
    [ReturnProductColumnKey.WARRANTY_ASSESSMENTS]: {
      header: "Warranty Assessments",
      cell: (item) => item.stringFields.warrantyAssessments ?? "",
    },
    [ReturnProductColumnKey.LABEL_STATUS]: {
      header: "Label Status",
      cell: (item) => item.stringFields.labelStatus ?? "",
    },
    [ReturnProductColumnKey.MOST_RECENT_LABEL_UPDATE]: {
      header: "Most Recent Label Update",
      cell: (item) =>
        item.dateFields.mostRecentLabelUpdate
          ? formatDateAccordingToTimezone(
              item.dateFields.mostRecentLabelUpdate,
              timezone,
            )
          : "",
    },
  };
};

export const getOrderProductCSVColumns = (
  timezone: string,
): {
  [K in OrderProductColumnKey]?: {
    header: string;
    cell: (item: OrderProductData) => string;
  };
} => {
  return {
    [OrderProductColumnKey.ORDER_NAME]: {
      header: "Order Number",
      cell: (item) => item.enumFields.orderName ?? "",
    },
    [OrderProductColumnKey.SHOPIFY_ORDER_ID]: {
      header: "Provider Order ID",
      cell: (item) => item.enumFields.shopifyOrderId ?? "",
    },
    [OrderProductColumnKey.ORDER_PROCESSED_AT]: {
      header: "Order Processed At",
      cell: (item) =>
        item.dateFields.orderProcessedAt
          ? formatDateAccordingToTimezone(
              item.dateFields.orderProcessedAt,
              timezone,
            )
          : "",
    },
    [OrderProductColumnKey.ORDER_CANCELLED_AT]: {
      header: "Order Cancelled At",
      cell: (item) =>
        item.dateFields.orderCancelledAt
          ? formatDateAccordingToTimezone(
              item.dateFields.orderCancelledAt,
              timezone,
            )
          : "",
    },
    [OrderProductColumnKey.ORDER_PROCESSED_DATE]: {
      header: "Order Processed Date",
      cell: (item) =>
        item.dateFields.orderProcessedDate
          ? formatDateAccordingToTimezone(
              item.dateFields.orderProcessedDate,
              timezone,
            )
          : "",
    },
    [OrderProductColumnKey.ORDER_CANCELLED_DATE]: {
      header: "Order Cancelled Date",
      cell: (item) =>
        item.dateFields.orderCancelledDate
          ? formatDateAccordingToTimezone(
              item.dateFields.orderCancelledDate,
              timezone,
            )
          : "",
    },
    [OrderProductColumnKey.SALES_CHANNEL]: {
      header: "Sales Channel",
      cell: (item) => item.enumFields.salesChannel ?? "",
    },
    [OrderProductColumnKey.LINE_ITEM_ID]: {
      header: "Line Item ID",
      cell: (item) => item.enumFields.lineItemId ?? "",
    },
    [OrderProductColumnKey.PRODUCT_ID]: {
      header: "Product ID",
      cell: (item) => item.enumFields.productId ?? "",
    },
    [OrderProductColumnKey.VARIANT_ID]: {
      header: "Variant ID",
      cell: (item) => item.enumFields.variantId ?? "",
    },
    [OrderProductColumnKey.PRODUCT_TITLE]: {
      header: "Product Name",
      cell: (item) => item.enumFields.productTitle ?? "",
    },
    [OrderProductColumnKey.VARIANT_TITLE]: {
      header: "Variant Name",
      cell: (item) => item.enumFields.variantTitle ?? "",
    },
    [OrderProductColumnKey.SKU]: {
      header: "SKU",
      cell: (item) => item.enumFields.sku ?? "",
    },
    [OrderProductColumnKey.UPC]: {
      header: "UPC",
      cell: (item) => item.stringFields.upc ?? "",
    },
    [OrderProductColumnKey.PRODUCT_TYPE]: {
      header: "Product Type",
      cell: (item) => item.enumFields.productType ?? "",
    },
    [OrderProductColumnKey.PRODUCT_TAGS]: {
      header: "Product Tags",
      cell: (item) => item.stringFields.productTags?.join(";") ?? "",
    },
    [OrderProductColumnKey.VENDOR]: {
      header: "Vendor",
      cell: (item) => item.enumFields.vendor ?? "",
    },
    [OrderProductColumnKey.TAGS]: {
      header: "Tags",
      cell: (item) => item.stringFields.tags?.join(";") ?? "",
    },
    [OrderProductColumnKey.QUANTITY]: {
      header: "Quantity",
      cell: (item) =>
        item.numberFields.quantity !== undefined
          ? item.numberFields.quantity.toString()
          : "",
    },
    [OrderProductColumnKey.UNIT_PRICE]: {
      header: "Unit Price",
      cell: (item) =>
        item.numberFields.unitPrice !== undefined
          ? getCurrencyFormatter(item.enumFields.currency).format(
              item.numberFields.unitPrice,
            )
          : "",
    },
    [OrderProductColumnKey.PRICE]: {
      header: "Price",
      cell: (item) =>
        item.numberFields.price !== undefined
          ? getCurrencyFormatter(item.enumFields.currency).format(
              item.numberFields.price,
            )
          : "",
    },
    [OrderProductColumnKey.DISCOUNT_ALLOCATIONS]: {
      header: "Discount Allocations",
      cell: (item) =>
        item.numberFields.discountAllocations !== undefined
          ? getCurrencyFormatter(item.enumFields.currency).format(
              item.numberFields.discountAllocations,
            )
          : "",
    },
    [OrderProductColumnKey.TAX_PRICE]: {
      header: "Tax",
      cell: (item) =>
        item.numberFields.taxPrice !== undefined
          ? getCurrencyFormatter(item.enumFields.currency).format(
              item.numberFields.taxPrice,
            )
          : "",
    },
    [OrderProductColumnKey.CUSTOMER_EMAIL]: {
      header: "Customer Email",
      cell: (item) => item.enumFields.customerEmail ?? "",
    },
    [OrderProductColumnKey.CUSTOMER_NAME]: {
      header: "Customer Name",
      cell: (item) => item.enumFields.customerName ?? "",
    },
    [OrderProductColumnKey.IS_CUSTOMER_FIRST_ORDER]: {
      header: "Is Customer First Order",
      cell: (item) => item.booleanFields.isCustomerFirstOrder?.toString() ?? "",
    },
    [OrderProductColumnKey.ORDER_COUNTRY_CODE]: {
      header: "Order Country Code",
      cell: (item) => item.enumFields.orderCountryCode ?? "",
    },
    [OrderProductColumnKey.FINANCIAL_STATUS]: {
      header: "Financial Status",
      cell: (item) => item.enumFields.financialStatus ?? "",
    },
    [OrderProductColumnKey.IS_EXCHANGE_ORDER]: {
      header: "Is Exchange Order",
      cell: (item) => item.booleanFields.isExchangeOrder?.toString() ?? "",
    },
    [OrderProductColumnKey.IS_PROTECTED]: {
      header: "Is Protected",
      cell: (item) => item.booleanFields.isProtected?.toString() ?? "",
    },
    [OrderProductColumnKey.IS_REDO_ELIGIBLE]: {
      header: "Is Redo Eligible",
      cell: (item) => item.booleanFields.isRedoEligible?.toString() ?? "",
    },
    [OrderProductColumnKey.IS_PACKAGE_PROTECTED]: {
      header: "Is Package Protected",
      cell: (item) => item.booleanFields.isPackageProtected?.toString() ?? "",
    },
    [OrderProductColumnKey.FULFILLMENT_LOCATION_ID]: {
      header: "Fulfillment Location ID",
      cell: (item) => item.enumFields.fulfillmentLocationId ?? "",
    },
    [OrderProductColumnKey.ORDER_TRACKING_NUMBERS]: {
      header: "Order Tracking Numbers",
      cell: (item) => item.stringFields.orderTrackingNumbers?.join(";") ?? "",
    },
    [OrderProductColumnKey.FULFILLMENT_SHIPMENT_STATUS]: {
      header: "Fulfillment Shipment Status",
      cell: (item) => item.enumFields.fulfillmentShipmentStatus ?? "",
    },
    [OrderProductColumnKey.ORDER_ITEM_DELIVERED_AT]: {
      header: "Order Item Delivered At",
      cell: (item) =>
        item.dateFields.orderItemDeliveredAt
          ? formatDateAccordingToTimezone(
              item.dateFields.orderItemDeliveredAt,
              timezone,
            )
          : "",
    },
    [OrderProductColumnKey.ORDER_ITEM_DELIVERED_DATE]: {
      header: "Order Item Delivered Date",
      cell: (item) =>
        item.dateFields.orderItemDeliveredDate
          ? formatDateAccordingToTimezone(
              item.dateFields.orderItemDeliveredDate,
              timezone,
            )
          : "",
    },
    [OrderProductColumnKey.TOTAL_QUANTITY_RETURNED]: {
      header: "Total Quantity Returned",
      cell: (item) =>
        item.numberFields.totalQuantityReturned !== undefined
          ? item.numberFields.totalQuantityReturned.toString()
          : "",
    },
    [OrderProductColumnKey.TOTAL_RETURN_PRICE]: {
      header: "Total Return Price",
      cell: (item) =>
        item.numberFields.totalReturnPrice !== undefined
          ? getCurrencyFormatter(item.enumFields.currency).format(
              item.numberFields.totalReturnPrice,
            )
          : "",
    },
    [OrderProductColumnKey.TOTAL_RETURN_REFUND_VALUE]: {
      header: "Total Return Refund Value",
      cell: (item) =>
        item.numberFields.totalReturnRefundValue !== undefined
          ? getCurrencyFormatter(item.enumFields.currency).format(
              item.numberFields.totalReturnRefundValue,
            )
          : "",
    },
    [OrderProductColumnKey.TOTAL_RETURN_STORE_CREDIT_VALUE]: {
      header: "Total Return Store Credit Value",
      cell: (item) =>
        item.numberFields.totalReturnStoreCreditValue !== undefined
          ? getCurrencyFormatter(item.enumFields.currency).format(
              item.numberFields.totalReturnStoreCreditValue,
            )
          : "",
    },
    [OrderProductColumnKey.TOTAL_RETURN_EXCHANGE_VALUE]: {
      header: "Total Return Exchange Value",
      cell: (item) =>
        item.numberFields.totalReturnExchangeValue !== undefined
          ? getCurrencyFormatter(item.enumFields.currency).format(
              item.numberFields.totalReturnExchangeValue,
            )
          : "",
    },
    [OrderProductColumnKey.TOTAL_VARIANT_EXCHANGE_VALUE]: {
      header: "Total Variant Exchange Value",
      cell: (item) =>
        item.numberFields.totalVariantExchangeValue !== undefined
          ? getCurrencyFormatter(item.enumFields.currency).format(
              item.numberFields.totalVariantExchangeValue,
            )
          : "",
    },
    [OrderProductColumnKey.TOTAL_ADVANCED_EXCHANGE_VALUE]: {
      header: "Total Advanced Exchange Value",
      cell: (item) =>
        item.numberFields.totalAdvancedExchangeValue !== undefined
          ? getCurrencyFormatter(item.enumFields.currency).format(
              item.numberFields.totalAdvancedExchangeValue,
            )
          : "",
    },
    [OrderProductColumnKey.TOTAL_RETURN_TAX]: {
      header: "Total Return Tax",
      cell: (item) =>
        item.numberFields.totalReturnTax !== undefined
          ? getCurrencyFormatter(item.enumFields.currency).format(
              item.numberFields.totalReturnTax,
            )
          : "",
    },
    [OrderProductColumnKey.TOTAL_RETURN_ADJUSTMENT]: {
      header: "Total Return Adjustment",
      cell: (item) =>
        item.numberFields.totalReturnAdjustment !== undefined
          ? getCurrencyFormatter(item.enumFields.currency).format(
              item.numberFields.totalReturnAdjustment,
            )
          : "",
    },
    [OrderProductColumnKey.TOTAL_RETURN_PRODUCT_VALUE]: {
      header: "Total Return Product Value",
      cell: (item) =>
        item.numberFields.totalReturnProductValue !== undefined
          ? getCurrencyFormatter(item.enumFields.currency).format(
              item.numberFields.totalReturnProductValue,
            )
          : "",
    },
    [OrderProductColumnKey.TOTAL_RETURN_VALUE_NO_TAX_NO_ADJUSTMENT]: {
      header: "Total Return Value (No Tax/Adjustment)",
      cell: (item) =>
        item.numberFields.totalReturnValueNoTaxNoAdjustment !== undefined
          ? getCurrencyFormatter(item.enumFields.currency).format(
              item.numberFields.totalReturnValueNoTaxNoAdjustment,
            )
          : "",
    },
    [OrderProductColumnKey.HAS_PRODUCT_VARIANT_EXCHANGE]: {
      header: "Has Product Variant Exchange",
      cell: (item) =>
        item.booleanFields.hasProductVariantExchange?.toString() ?? "",
    },
    [OrderProductColumnKey.HAS_PRODUCT_ADVANCED_EXCHANGE]: {
      header: "Has Product Advanced Exchange",
      cell: (item) =>
        item.booleanFields.hasProductAdvancedExchange?.toString() ?? "",
    },
    [OrderProductColumnKey.RETURN_IDS]: {
      header: "Return IDs",
      cell: (item) => item.stringFields.returnIds?.join(";") ?? "",
    },
    [OrderProductColumnKey.RETURN_LINE_ITEM_IDS]: {
      header: "Return Line Item IDs",
      cell: (item) => item.stringFields.returnLineItemIds?.join(";") ?? "",
    },
    [OrderProductColumnKey.COMPENSATION_METHODS]: {
      header: "Compensation Methods",
      cell: (item) => item.stringFields.compensationMethods?.join(";") ?? "",
    },
    [OrderProductColumnKey.RETURN_TYPES]: {
      header: "Return Types",
      cell: (item) => item.stringFields.returnTypes?.join(";") ?? "",
    },
    [OrderProductColumnKey.RETURN_REASONS]: {
      header: "Return Reasons",
      cell: (item) => item.stringFields.returnReasons?.join(";") ?? "",
    },
    [OrderProductColumnKey.RETURN_METHODS]: {
      header: "Return Methods",
      cell: (item) => item.stringFields.returnMethods?.join(";") ?? "",
    },
    [OrderProductColumnKey.RETURN_PRODUCT_STATUSES]: {
      header: "Return Product Statuses",
      cell: (item) => item.stringFields.returnProductStatuses?.join(";") ?? "",
    },
    [OrderProductColumnKey.EXCHANGE_GROUP_ITEMS]: {
      header: "Exchange Group Items",
      cell: (item) =>
        item.objectArrayFields.exchangeGroupItems
          ?.map((x) =>
            [x.title, x.productId, x.variantId].filter(Boolean).join("/"),
          )
          .join(";") ?? "",
    },
    [OrderProductColumnKey.EXCHANGE_FOR_ITEMS]: {
      header: "Exchange For Items",
      cell: (item) =>
        item.objectArrayFields.exchangeForItems
          ?.map((x) =>
            [x.productTitle, x.productId, x.variantId]
              .filter(Boolean)
              .join("/"),
          )
          .join(";") ?? "",
    },
    [OrderProductColumnKey.EXCHANGE_TYPES]: {
      header: "Exchange Types",
      cell: (item) => item.stringFields.exchangeTypes?.join(";") ?? "",
    },
    [OrderProductColumnKey.ADVANCED_EXCHANGE_ITEMS]: {
      header: "Advanced Exchange Items",
      cell: (item) =>
        item.objectArrayFields.advancedExchangeItems
          ?.map((x) => [x.title, x.variantId].filter(Boolean).join("/"))
          .join(";") ?? "",
    },
    [OrderProductColumnKey.RETURN_TO_MERCHANT_DELIVERED_DATES]: {
      header: "Return to Merchant Delivered Dates",
      cell: (item) =>
        item.stringFields.returnToMerchantDeliveredDates?.join(";") ?? "",
    },
    [OrderProductColumnKey.RETURN_REJECT_REASONS]: {
      header: "Return Reject Reasons",
      cell: (item) => item.stringFields.returnRejectReasons?.join(";") ?? "",
    },
    [OrderProductColumnKey.DISPOSITION_MERCHANT_NOTES]: {
      header: "Disposition Merchant Notes",
      cell: (item) =>
        item.stringFields.dispositionMerchantNotes?.join(";") ?? "",
    },
    [OrderProductColumnKey.DISPOSITION_MERCHANT_GRADES]: {
      header: "Disposition Merchant Grades",
      cell: (item) =>
        item.stringFields.dispositionMerchantGrades?.join(";") ?? "",
    },
    [OrderProductColumnKey.DISPOSITION_MERCHANT_OUTCOMES]: {
      header: "Disposition Merchant Outcomes",
      cell: (item) =>
        item.stringFields.dispositionMerchantOutcomes?.join(";") ?? "",
    },
  };
};

export const getWarrantyCustomerDataCSVColumns = (
  timezone: string,
): {
  [K in WarrantyCustomerDataColumnKey]?: {
    header: string;
    cell: (item: WarrantyCustomerData) => string;
  };
} => {
  return {
    [WarrantyCustomerDataColumnKey.CUSTOMER_NAME]: {
      header: "Customer Name",
      cell: (item: WarrantyCustomerData) => item.customerName ?? "",
    },
    [WarrantyCustomerDataColumnKey.CUSTOMER_EMAIL]: {
      header: "Customer Email",
      cell: (item: WarrantyCustomerData) => item.customerEmail,
    },
    [WarrantyCustomerDataColumnKey.WARRANTY_NAME]: {
      header: "Warranty Name",
      cell: (item: WarrantyCustomerData) => item.warrantyName,
    },
    [WarrantyCustomerDataColumnKey.PRODUCT_NAME]: {
      header: "Product Name",
      cell: (item: WarrantyCustomerData) => item.productName,
    },
    [WarrantyCustomerDataColumnKey.VARIANT_NAME]: {
      header: "Variant Name",
      cell: (item: WarrantyCustomerData) => item.variantName,
    },
    [WarrantyCustomerDataColumnKey.WARRANTY_REGISTRATION_DATE]: {
      header: "Warranty Registration Date",
      cell: (item: WarrantyCustomerData) =>
        formatDateAccordingToTimezone(item.warrantyRegistrationDate, timezone),
    },
    [WarrantyCustomerDataColumnKey.CUSTOMER_PHONE]: {
      header: "Customer Phone",
      cell: (item: WarrantyCustomerData) => item.customerPhone ?? "",
    },
    [WarrantyCustomerDataColumnKey.CUSTOMER_ADDRESS]: {
      header: "Customer Address",
      cell: (item: WarrantyCustomerData) => item.customerAddress ?? "",
    },
    [WarrantyCustomerDataColumnKey.CUSTOMER_SECOND_ADDRESS]: {
      header: "Customer Second Address",
      cell: (item: WarrantyCustomerData) => item.customerSecondAddress ?? "",
    },
    [WarrantyCustomerDataColumnKey.CUSTOMER_CITY]: {
      header: "Customer City",
      cell: (item: WarrantyCustomerData) => item.customerCity ?? "",
    },
    [WarrantyCustomerDataColumnKey.CUSTOMER_PROVINCE]: {
      header: "Customer Province",
      cell: (item: WarrantyCustomerData) => item.customerProvince ?? "",
    },
    [WarrantyCustomerDataColumnKey.CUSTOMER_POSTAL]: {
      header: "Customer Postal",
      cell: (item: WarrantyCustomerData) => item.customerPostal ?? "",
    },
    [WarrantyCustomerDataColumnKey.OPTED_INTO_MARKETING]: {
      header: "Opted Into Marketing",
      cell: (item: WarrantyCustomerData) =>
        item.optedIntoMarketing?.toString() ?? "",
    },
    [WarrantyCustomerDataColumnKey.SOURCE]: {
      header: "Source",
      cell: (item: WarrantyCustomerData) => item.source ?? "",
    },
    [WarrantyCustomerDataColumnKey.SOURCE_NAME]: {
      header: "Source Name",
      cell: (item: WarrantyCustomerData) => item.channelName ?? "",
    },
    [WarrantyCustomerDataColumnKey.ORDER_NUMBER]: {
      header: "Order#",
      cell: (item: WarrantyCustomerData) => item.orderNumber ?? "",
    },
  };
};

export const getPurchasedExtendedWarrantyDataCSVColumns = (
  timezone: string,
): {
  [K in PurchasedExtendedWarrantyDataColumnKey]?: {
    header: string;
    cell: (item: PurchasedExtendedWarrantyData) => string;
  };
} => {
  return {
    [PurchasedExtendedWarrantyDataColumnKey.ORDER_ID]: {
      header: "Order ID",
      cell: (item: PurchasedExtendedWarrantyData) => item.orderId ?? "",
    },
    [PurchasedExtendedWarrantyDataColumnKey.COMPANY_NAME]: {
      header: "Company Name",
      cell: (item: PurchasedExtendedWarrantyData) => item.companyName ?? "",
    },
    [PurchasedExtendedWarrantyDataColumnKey.NAME]: {
      header: "Name",
      cell: (item: PurchasedExtendedWarrantyData) => item.name ?? "",
    },
    [PurchasedExtendedWarrantyDataColumnKey.ADDRESS1]: {
      header: "Address 1",
      cell: (item: PurchasedExtendedWarrantyData) => item.address1 ?? "",
    },
    [PurchasedExtendedWarrantyDataColumnKey.ADDRESS2]: {
      header: "Address 2",
      cell: (item: PurchasedExtendedWarrantyData) => item.address2 ?? "",
    },
    [PurchasedExtendedWarrantyDataColumnKey.ORDER_DATE]: {
      header: "Order Date",
      cell: (item: PurchasedExtendedWarrantyData) =>
        item.orderDate
          ? formatDateAccordingToTimezone(item.orderDate, timezone)
          : "",
    },
    [PurchasedExtendedWarrantyDataColumnKey.ACTIVATION_DATE]: {
      header: "Activation Date",
      cell: (item: PurchasedExtendedWarrantyData) =>
        item.activationDate
          ? formatDateAccordingToTimezone(item.activationDate, timezone)
          : "",
    },
    [PurchasedExtendedWarrantyDataColumnKey.EXPIRATION_DATE]: {
      header: "Expiration Date",
      cell: (item: PurchasedExtendedWarrantyData) =>
        item.expirationDate
          ? formatDateAccordingToTimezone(item.expirationDate, timezone)
          : "",
    },
    [PurchasedExtendedWarrantyDataColumnKey.WARRANTY_PRICE]: {
      header: "Total Paid for Warranties",
      cell: (item: PurchasedExtendedWarrantyData) =>
        attemptToFormatCurrency(item.warrantyPrice, item.shopCurrency),
    },
    [PurchasedExtendedWarrantyDataColumnKey.WARRANTY_UNIT_PRICE]: {
      header: "Warranty Unit Price",
      cell: (item: PurchasedExtendedWarrantyData) =>
        attemptToFormatCurrency(item.warrantyUnitPrice, item.shopCurrency),
    },
    [PurchasedExtendedWarrantyDataColumnKey.WARRANTY_QUANTITY]: {
      header: "Warranty Quantity",
      cell: (item: PurchasedExtendedWarrantyData) =>
        item.warrantyQuantity ? String(item.warrantyQuantity) : "",
    },
    [PurchasedExtendedWarrantyDataColumnKey.ITEM_PRICE]: {
      header: "Item Price",
      cell: (item: PurchasedExtendedWarrantyData) =>
        attemptToFormatCurrency(item.itemPrice, item.shopCurrency),
    },
    [PurchasedExtendedWarrantyDataColumnKey.ITEM_SKU]: {
      header: "Item SKU",
      cell: (item: PurchasedExtendedWarrantyData) => item.itemSku ?? "",
    },
    [PurchasedExtendedWarrantyDataColumnKey.ITEM_NAME]: {
      header: "Item Name",
      cell: (item: PurchasedExtendedWarrantyData) => item.itemName ?? "",
    },
    [PurchasedExtendedWarrantyDataColumnKey.TERM_PURCHASED]: {
      header: "Term Purchased",
      cell: (item: PurchasedExtendedWarrantyData) => item.termPurchased ?? "",
    },
    [PurchasedExtendedWarrantyDataColumnKey.DELIVERY_DATE]: {
      header: "Delivery Date",
      cell: (item: PurchasedExtendedWarrantyData) =>
        item.deliveryDate
          ? formatDateAccordingToTimezone(item.deliveryDate, timezone)
          : "",
    },
    [PurchasedExtendedWarrantyDataColumnKey.PHONE_NUMBER]: {
      header: "Phone Number",
      cell: (item: PurchasedExtendedWarrantyData) => item.phoneNumber ?? "",
    },
    [PurchasedExtendedWarrantyDataColumnKey.EMAIL]: {
      header: "Email",
      cell: (item: PurchasedExtendedWarrantyData) => item.email ?? "",
    },
    [PurchasedExtendedWarrantyDataColumnKey.WARRANTY_PURCHASE_DATE]: {
      header: "Warranty Purchase Date",
      cell: (item: PurchasedExtendedWarrantyData) =>
        item.warrantyPurchaseDate
          ? formatDateAccordingToTimezone(item.warrantyPurchaseDate, timezone)
          : "",
    },
    [PurchasedExtendedWarrantyDataColumnKey.CITY]: {
      header: "City",
      cell: (item: PurchasedExtendedWarrantyData) => item.city ?? "",
    },
    [PurchasedExtendedWarrantyDataColumnKey.STATE]: {
      header: "State",
      cell: (item: PurchasedExtendedWarrantyData) => item.state ?? "",
    },
    [PurchasedExtendedWarrantyDataColumnKey.ZIP]: {
      header: "Zip",
      cell: (item: PurchasedExtendedWarrantyData) => item.zip ?? "",
    },
    [PurchasedExtendedWarrantyDataColumnKey.COUNTRY]: {
      header: "Country",
      cell: (item: PurchasedExtendedWarrantyData) => item.country ?? "",
    },
    [PurchasedExtendedWarrantyDataColumnKey.REV_SHARE_PRICE]: {
      header: "Estimated Rev Share",
      cell: (item: PurchasedExtendedWarrantyData) =>
        attemptToFormatCurrency(item.revSharePrice, item.shopCurrency),
    },
  };
};

export { SalesChannelValues } from "./returns-claims/filters/sales-channel-values";
